import { cloneDeep, isUndefined } from 'lodash';
import { rgbaToHexa } from '@meitu/saas-colors';
// 初始化文字需要的依赖
import { createCanvas, fixZipResUrl, waterMarkBridge } from '@meitu/wasm-lib';
import { Layer } from '@/layers/layer';
import { hexToRgbaArr } from '@/utils/color-kit';
import {
    IFont,
    LayerType,
    MaterialIdsType,
    PluginType,
    type IExportPlugin,
    type ILayerProps,
    type IWatermarkLayerProps,
} from '@/version-types';
import watermarkLayerBehavior from './behavior';
import {
    AR_RES_ID,
    AR_RES_PATH,
    FULL_SCREEN_TYPE,
    getDefaultWatermarkLayerData,
    WATERMARK_DEFAULT_CROSS,
    WATERMARK_DEFAULT_SIZE,
    WATERMARK_DEFAULT_SPACING,
    WATERMARK_HISTORY_KEY,
    WATERMARK_TEXTS_MAX,
    WATERMARK_TILE_BASE_SIZE,
    type WATERMARK_TYPE,
} from './constants';
import { computedPosition } from './utils/watermark';
import { WatermarkLayerHistory } from './watermark-history';

/** 水印图层的实例级业务入口。 */
export class WatermarkLayer extends Layer {
    /** 水印 WASM 桥接是否已完成初始化。 */
    private isInitialized: boolean;
    /** 全屏状态变化使用的实例内部事件名。 */
    private fullScreenChangedEvent = 'watermark-fullscreen-changed-event';

    constructor(identity: string) {
        super(identity);
        this.isInitialized = false;
    }

    private async runWatermarkAndGetResult(stageIndex: number, watermarkLayer: ILayerProps) {
        const { data, materialIds, rotate } = watermarkLayer;
        const { originUrl, isFullScreen, space, cross, offsetX, offsetY } = data as IWatermarkLayerProps;
        const materialId = materialIds?.[MaterialIdsType.WATERMARK]?.[0];
        await this.initializedBridge(stageIndex);
        await this.createWatermarkInfo(watermarkLayer.id);
        // 不设置字体,默认为图片水印
        if (!materialId && originUrl) {
            await this.setImgWatermarkInfo(watermarkLayer, originUrl);
        } else {
            await this.setTextWatermarkInfo(watermarkLayer);
        }
        if (isFullScreen) {
            await waterMarkBridge.setFullscreen(watermarkLayer.id, true);
            await waterMarkBridge.setWidthRatio(watermarkLayer.id, WATERMARK_TILE_BASE_SIZE / 100, 0);
            await waterMarkBridge.setSpacing(watermarkLayer.id, space, 0);
            await waterMarkBridge.setRotate(watermarkLayer.id, rotate, 0);
            await waterMarkBridge.setStaggered(watermarkLayer.id, cross, 0);
            await waterMarkBridge.setMoveOffsetX(watermarkLayer.id, offsetX);
            await waterMarkBridge.setMoveOffsetY(watermarkLayer.id, offsetY);
        }

        await waterMarkBridge.updateResultBuffer(watermarkLayer.id);
        const result = (await this.getWatermarkResult(watermarkLayer.id)) as {
            url: string;
            width: number;
            height: number;
        };

        return result;
    }

    async batchChangeTextWatermark(
        texts: string[],
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).texts = texts;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchAddWatermarkLayer(
        type: WATERMARK_TYPE,
        stageIndexList: number[],
        url?: string,
        textProps?: {
            materialId: string;
            zipUrl: string;
        },
        isFullScreen?: boolean,
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const addLayers: ILayerProps[] = [];
        const newLayers: ILayerProps[] = [];
        const oldLayers: ILayerProps[] = [];
        this.framework.service.history.pushFunc('18_88');
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK)!;
            oldLayers.push(cloneDeep(watermarkLayer));
            const layer = getDefaultWatermarkLayerData(type, url, textProps);
            if (watermarkLayer) {
                layer.id = watermarkLayer.id;
            }

            if (isFullScreen) {
                Object.assign(layer, {
                    width: bgLayer.width,
                    height: bgLayer.height,
                    top: 0,
                    left: 0,
                    rotate: 0,
                    data: {
                        ...layer.data,
                        ...{
                            isFullScreen: true,
                            size: 47 / 100,
                            space: WATERMARK_DEFAULT_SPACING / 100,
                            cross: WATERMARK_DEFAULT_CROSS / 100,
                        },
                    },
                });
            }

            const result = await this.runWatermarkAndGetResult(stageIndex, layer);
            (layer.data as IWatermarkLayerProps).url = result?.url!;

            if (!isFullScreen) {
                Object.assign(
                    layer,
                    computedPosition(
                        { width: result.width, height: result.height },
                        {
                            width: bgLayer.width,
                            height: bgLayer.height,
                        },
                    ),
                );
            }
            if (!watermarkLayer) {
                addLayers.push(layer);
            } else {
                newLayers.push(cloneDeep(layer));
                this.framework.service.frame.addOrUpdateLayer(layer, stageIndex);
            }
            setStageLoading?.(stageIndex, false);
        }

        return addLayers;
    }

    async batchSetWatermarkFullScreen(
        type: FULL_SCREEN_TYPE,
        value: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            if (type === FULL_SCREEN_TYPE.SIZE) {
                (newLayer?.data as IWatermarkLayerProps).size = value / 100;
            } else if (type === FULL_SCREEN_TYPE.SPACE) {
                (newLayer.data as IWatermarkLayerProps).space = value / 100;
            } else if (type === FULL_SCREEN_TYPE.ROTATE) {
                newLayer.rotate = value;
            } else if (type === FULL_SCREEN_TYPE.CROSS) {
                (newLayer.data as IWatermarkLayerProps).cross = value / 100;
            } else if (type === FULL_SCREEN_TYPE.OFFSET_X) {
                (newLayer.data as IWatermarkLayerProps).offsetX = value / 100;
            } else if (type === FULL_SCREEN_TYPE.OFFSET_Y) {
                (newLayer.data as IWatermarkLayerProps).offsetY = value / 100;
            }
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchSetWatermarkPosition(
        position: string,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const bgLayer = stageList[i].find((layer) => layer.layerType === LayerType.BACKGROUND_LAYER)!;
            const stageSize = {
                width: bgLayer.width,
                height: bgLayer.height,
            };
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            const spaceSize = {
                width: 0.1 * stageSize.width,
                height: 0.1 * stageSize.height,
            };
            if (position === 'top-left') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: spaceSize.width,
                });
            } else if (position === 'top-center') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'top-right') {
                Object.assign(newLayer, {
                    top: spaceSize.height,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            } else if (position === 'center-left') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: spaceSize.width,
                });
            } else if (position === 'center-center') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'center-right') {
                Object.assign(newLayer, {
                    top: stageSize.height / 2 - watermarkLayer.height / 2,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            } else if (position === 'bottom-left') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: spaceSize.width,
                });
            } else if (position === 'bottom-center') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: stageSize.width / 2 - watermarkLayer.width / 2,
                });
            } else if (position === 'bottom-right') {
                Object.assign(newLayer, {
                    top: stageSize.height - watermarkLayer.height - spaceSize.height,
                    left: stageSize.width - watermarkLayer.width - spaceSize.width,
                });
            }

            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchSetTextWatermarkColor(
        { r, g, b, a },
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const { id } = watermarkLayer;
            const newLayer = cloneDeep(watermarkLayer);
            const { texts } = newLayer.data as IWatermarkLayerProps;

            for (let i = 0; i < texts.length; i++) {
                await waterMarkBridge.setTextColor(id, r, g, b, a, i);
            }
            Object.assign(newLayer.data, {
                textColor: rgbaToHexa({
                    r,
                    g,
                    b,
                    a: a / 100,
                }),
            });
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchSetOpacity(
        opacity: number,
        stageIndexList: number[],
        originOpacity?: number | null,
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            setStageLoading?.(stageIndex, true);
            const newLayer = cloneDeep(watermarkLayer);
            newLayer.opacity = opacity;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchSetWatermarkFont(
        fontFamily: string,
        fontFamilyId: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
            await this.loadTextFamily(fontFamily);
            (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
            const materialIds = newLayer.materialIds || {};
            materialIds[MaterialIdsType.FONT_FAMILY] = [String(fontFamilyId)];
            newLayer.materialIds = materialIds;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async batchRemoveWatermarkLayer(stageIndexList: number[]) {
        const stageList = this.framework.service.frame.getFrames();
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            const layers = this.framework.service.frame.getLayers(stageIndex);
            for (let j = 0; j < layers.length; j++) {
                const layer = layers[j];
                if (layer.layerType === LayerType.WATERMARK) {
                    await waterMarkBridge.removeWaterMark(layer.id);
                    this.framework.service.frame.delLayer(layer.id);
                }
            }
        }
    }

    async batchUpdateBase(
        size: number,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            if (!watermarkLayer) continue;
            oldLayers.push(cloneDeep(watermarkLayer));
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).size = size;
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            newLayers.push(cloneDeep(newLayer));
            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }
        this.framework.service.history.write(oldLayers, newLayers);
    }

    async setBatchFullScreen(
        isFullScreen: boolean,
        stageIndexList: number[],
        setStageLoading?: (stageIndex: number, loading: boolean) => void,
    ) {
        const stageList = this.framework.service.frame.getFrames();
        const oldLayers = [] as ILayerProps[];
        const newLayers = [] as ILayerProps[];
        for (let i = 0; i < stageList.length; i++) {
            const stageIndex = stageIndexList[i];
            setStageLoading?.(stageIndex, true);
            const watermarkLayer = stageList[i].find((layer) => layer.layerType === LayerType.WATERMARK);
            const mainLayer = stageList[i].find((layer) => layer.layerType === LayerType.MAIN_LAYER)!;
            if (!watermarkLayer) continue;
            const newLayer = cloneDeep(watermarkLayer);
            (newLayer.data as IWatermarkLayerProps).isFullScreen = isFullScreen;
            oldLayers.push(cloneDeep(watermarkLayer));
            newLayers.push(cloneDeep(newLayer));
            const { width, height } = mainLayer;
            if (isFullScreen) {
                Object.assign(newLayer, {
                    width,
                    height,
                    top: 0,
                    left: 0,
                    rotate: 0,
                    data: {
                        ...newLayer.data,
                        ...{
                            url: '',
                            isFullScreen: true,
                            size: 47 / 100,
                            space: WATERMARK_DEFAULT_SPACING / 100,
                            cross: WATERMARK_DEFAULT_CROSS / 100,
                        },
                    },
                });
            } else {
                // 设置非全屏
                Object.assign(newLayer, {
                    rotate: 0,
                    data: {
                        ...newLayer.data,
                        ...{
                            url: '',
                            size: 0,
                            space: 0,
                            rotate: 0,
                            cross: 0,
                        },
                    },
                });
            }
            const result = await this.runWatermarkAndGetResult(stageIndex, newLayer);
            (newLayer.data as IWatermarkLayerProps).url = result?.url!;
            if (!isFullScreen) {
                Object.assign(
                    newLayer,
                    computedPosition({ width: result!.width, height: result!.height }, { width, height }),
                    {
                        rotate: 0,
                    },
                );
            }

            this.framework.service.frame.addOrUpdateLayer(newLayer);
            setStageLoading?.(stageIndex, false);
        }

        this.framework.service.history.write(oldLayers, newLayers);
    }

    /** 修改水印文本 */
    async changeTextWatermark(id: string, texts: any[], rgba: number[], fontFamily: string) {
        const oldLayer = this.framework.service.flatLayer.getLayer(id, true);
        if (!oldLayer) return;

        await this.initializedBridge();
        await this.createWatermarkInfo(id);
        await this.updateBridge(oldLayer);

        await this.setTextWatermarkItems(id, texts, rgba, fontFamily);
        await waterMarkBridge.updateResultBuffer(id);

        const newLayer = cloneDeep(oldLayer);
        const result = await this.getWatermarkResult(id);
        Object.assign(newLayer.data, {
            url: result?.url || '',
            texts,
        });

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer.__originIndex,
        });
    }

    async setTextWatermarkColor(id: string, { r, g, b, a }, count: number) {
        const oldLayer = this.framework.service.flatLayer.getLayer(id, true);
        if (!oldLayer) return;

        await this.initializedBridge();
        await this.createWatermarkInfo(id);
        await this.updateBridge(oldLayer);

        for (let i = 0; i < count; i++) {
            await waterMarkBridge.setTextColor(id, r, g, b, a, i);
        }
        await waterMarkBridge.updateResultBuffer(id);

        const newLayer = cloneDeep(oldLayer);
        const result = await this.getWatermarkResult(id);
        Object.assign(newLayer.data, {
            url: result?.url || '',
            textColor: rgbaToHexa({
                r,
                g,
                b,
                a: a / 100,
            }),
        });

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer.__originIndex,
        });
    }

    async setWatermarkFullScreen(id: string, type: FULL_SCREEN_TYPE, value: number) {
        const oldLayer = this.framework.service.flatLayer.getLayer(id, true);
        if (!oldLayer) return;

        await this.initializedBridge();
        await this.createWatermarkInfo(id);
        await this.updateBridge(oldLayer);

        const newLayer = cloneDeep(oldLayer);
        await this.setFullscreenOption(id, type, value, newLayer);
        await waterMarkBridge.updateResultBuffer(id);

        const result = await this.getWatermarkResult(id);

        (newLayer.data as IWatermarkLayerProps).url = result?.url!;

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer.__originIndex,
        });
    }

    /** 添加/替换水印图层 */
    async addWatermarkLayer(
        type: WATERMARK_TYPE,
        stageSize: {
            width: number;
            height: number;
        },
        url?: string,
        textProps?: {
            materialId: string;
            zipUrl: string;
        },
        isFullScreen?: boolean,
    ) {
        const oldLayer = cloneDeep(this.getWatermarkLayer());
        const layer = getDefaultWatermarkLayerData(type, url, textProps);
        // TODO:替换逻辑,id要同步为一个
        if (oldLayer) {
            layer.id = oldLayer.id;
        }

        await this.initializedBridge();
        await this.createWatermarkInfo(layer.id);

        // 不设置字体,默认为图片水印
        if (!textProps?.materialId && url) {
            await this.setImgWatermarkInfo(layer, url);
        } else {
            const info = await this.setTextWatermarkInfo(layer);
            if (info) {
                const { texts, rgba, fontFamily } = info;
                Object.assign(layer.data, {
                    texts,
                    textColor: rgbaToHexa({
                        r: rgba[0],
                        g: rgba[1],
                        b: rgba[2],
                        a: rgba[3] / 100,
                    }),
                    fontFamily,
                });
            }
        }

        await waterMarkBridge.setFullscreen(layer.id, isFullScreen ?? false);

        console.log('addWatermarkLayer', { layer: cloneDeep(layer), oldLayer: cloneDeep(oldLayer) });

        if (isFullScreen) {
            await this.setFullscreenOption(layer.id, FULL_SCREEN_TYPE.ROTATE, 0);
            await this.setFullscreenOption(layer.id, FULL_SCREEN_TYPE.SPACE, WATERMARK_DEFAULT_SPACING);
            await this.setFullscreenOption(layer.id, FULL_SCREEN_TYPE.CROSS, WATERMARK_DEFAULT_CROSS);
            await this.setFullscreenOption(layer.id, FULL_SCREEN_TYPE.SIZE, WATERMARK_TILE_BASE_SIZE);
            Object.assign(layer, {
                width: stageSize.width,
                height: stageSize.height,
                top: 0,
                left: 0,
                rotate: 0,
                data: {
                    ...layer.data,
                    ...{
                        isFullScreen: true,
                        size: WATERMARK_DEFAULT_SIZE / 100,
                        space: WATERMARK_DEFAULT_SPACING / 100,
                        cross: WATERMARK_DEFAULT_CROSS / 100,
                    },
                },
            });
        }

        await waterMarkBridge.updateResultBuffer(layer.id);
        const result = (await this.getWatermarkResult(layer.id)) as {
            url: string;
            width: number;
            height: number;
        };
        (layer.data as IWatermarkLayerProps).url = result?.url!;

        if (!isFullScreen) {
            Object.assign(layer, computedPosition({ width: result.width, height: result.height }, stageSize));
        }

        const newLayer = this.framework.service.frame.addOrUpdateLayer(layer);

        if (!isFullScreen) {
            this.framework.service.frame.setActiveLayer(newLayer.id);
        } else {
            this.framework.service.frame.delActiveLayer();
        }

        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer?.__originIndex,
            trackFunc: '18_88',
        });

        this.emitFullScreenChanged(!!isFullScreen);
    }

    async setFullScreen(isFullScreen: boolean) {
        const watermarkLayer = this.getWatermarkLayer();
        if (!watermarkLayer) return;
        const { id } = watermarkLayer;
        await this.initializedBridge();
        await this.createWatermarkInfo(id);
        await this.updateBridge(watermarkLayer);

        const stageSize = this.framework.service.frame.getActiveFrameSize();
        const oldLayer = cloneDeep(watermarkLayer);
        const newLayer = this.framework.service.flatLayer.getLayer(watermarkLayer.id, true)!;
        (newLayer.data as IWatermarkLayerProps).isFullScreen = isFullScreen;

        // 设置全屏
        await waterMarkBridge.setFullscreen(id, isFullScreen);

        if (isFullScreen) {
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.ROTATE, watermarkLayer.rotate);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.SIZE, WATERMARK_TILE_BASE_SIZE);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.SPACE, WATERMARK_DEFAULT_SPACING);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.CROSS, WATERMARK_DEFAULT_CROSS);

            await waterMarkBridge.updateResultBuffer(id);
            const result = await this.getWatermarkResult(id);
            const originIndex = this.framework.service.frame.getActiveFrameId();
            const { width, height } = this.framework.service.frame.getMainLayer(originIndex)!;
            Object.assign(newLayer, {
                width,
                height,
                top: 0,
                left: 0,
                rotate: watermarkLayer.rotate,
                data: {
                    ...newLayer.data,
                    ...{
                        url: result?.url || '',
                        size: WATERMARK_DEFAULT_SIZE / 100,
                        space: WATERMARK_DEFAULT_SPACING / 100,
                        cross: WATERMARK_DEFAULT_CROSS / 100,
                    },
                },
            });
        } else {
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.ROTATE, 0);

            await waterMarkBridge.updateResultBuffer(id);
            const result = await this.getWatermarkResult(id);
            // 设置非全屏
            Object.assign(newLayer, computedPosition({ width: result!.width, height: result!.height }, stageSize), {
                rotate: 0,
                data: {
                    ...newLayer.data,
                    ...{
                        url: result?.url || '',
                        size: 0,
                        space: 0,
                        rotate: 0,
                        cross: 0,
                    },
                },
            });
        }

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer?.__originIndex,
        });

        if (!isFullScreen) {
            this.framework.service.frame.setActiveLayer(id);
        } else {
            this.framework.service.frame.delActiveLayer();
        }

        this.emitFullScreenChanged(isFullScreen);
    }

    async setWatermarkFont(id: string, fontFamily: string, fontFamilyId: number) {
        const oldLayer = this.framework.service.flatLayer.getLayer(id, true);
        if (!oldLayer) return;

        await this.initializedBridge();
        await this.createWatermarkInfo(id);
        await this.updateBridge(oldLayer);

        await this.loadTextFamily(fontFamily);
        for (let i = 0; i < WATERMARK_TEXTS_MAX; i++) {
            await waterMarkBridge.setTextFont(id, fontFamily, i);
        }
        await waterMarkBridge.updateResultBuffer(id);

        const newLayer = cloneDeep(oldLayer);
        const result = await this.getWatermarkResult(id);
        (newLayer.data as IWatermarkLayerProps).url = result?.url!;
        (newLayer.data as IWatermarkLayerProps).fontFamily = fontFamily;
        const materialIds = newLayer.materialIds || {};
        materialIds[MaterialIdsType.FONT_FAMILY] = [String(fontFamilyId)];
        newLayer.materialIds = materialIds;

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer?.__originIndex,
        });
    }

    async setImageWatermarkUrl(
        layer: ILayerProps,
        url: string,
        stageSize: {
            width: number;
            height: number;
        },
    ) {
        const oldLayer = this.framework.service.flatLayer.getLayer(layer.id, true);
        if (!oldLayer) return;

        await this.initializedBridge();
        await this.createWatermarkInfo(layer.id);
        await this.setImgWatermarkInfo(layer, url);

        await waterMarkBridge.updateResultBuffer(layer.id);
        const result = (await this.getWatermarkResult(layer.id)) as {
            url: string;
            width: number;
            height: number;
        };

        const newLayer = cloneDeep(oldLayer);
        (newLayer.data as IWatermarkLayerProps).url = result.url!;
        (newLayer.data as IWatermarkLayerProps).originUrl = url!;
        Object.assign(newLayer, computedPosition({ width: result.width, height: result.height }, stageSize), {
            top: newLayer.top,
            left: newLayer.left,
        });

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer?.__originIndex,
        });
    }

    /** 更新水印图层 */
    async update(newLayer: ILayerProps, oldLayer: ILayerProps) {
        // const result = await this.getWatermarkResult(newLayer.id);
        // (newLayer.data as IWatermarkLayerProps).url = result?.url!;

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.framework.service.history.write({
            type: WATERMARK_HISTORY_KEY,
            data: {
                oldLayer,
                newLayer,
            },
            originIndex: oldLayer.__originIndex,
        });
    }

    /** 获取水印图层 */
    getWatermarkLayer(index?: number) {
        const frameId = index ?? this.framework.service.frame.getActiveFrameId();
        const layers = this.framework.service.frame.getLayers(frameId, false);
        return layers.find((layer) => layer.layerType === LayerType.WATERMARK) || null;
    }

    onFullScreenChanged(fn: (changed: boolean) => void) {
        return this.framework.service.events.on(this.fullScreenChangedEvent, fn);
    }

    private emitFullScreenChanged(changed: boolean) {
        this.framework.service.events.emit(this.fullScreenChangedEvent, changed);
    }

    private async loadTextFamily(fontFamily: string) {
        const fontList = this.framework.service.font.getFonts();
        const font = fontList.find((v) => v.name === fontFamily);
        if (font) {
            // const { zip_file, old_zip_file } = font as IFont;
            const url = font.zip;
            if (!url) throw new Error('Font zip url can not be empty');
            await waterMarkBridge.registerFont(fontFamily, url);
        } else {
            // demo加载默认斗鱼字体
            const zipFile = await import(`./assets/douyufont.zip`);
            await waterMarkBridge.registerFont('douyufont', zipFile.default);
        }
    }

    /** 把配置同步到底层 */
    private async updateBridge(layer: ILayerProps) {
        const { rotate, id } = layer;
        const {
            fontFamily,
            originUrl,
            textColor,
            isFullScreen,
            space = 0,
            cross = 0,
            offsetX = 0,
            offsetY = 0,
        } = layer.data as IWatermarkLayerProps;
        if (!fontFamily && !textColor) {
            await this.setImgWatermarkInfo(layer, originUrl);
        } else {
            await this.setTextWatermarkInfo(layer);
        }
        await waterMarkBridge.setFullscreen(id, !!isFullScreen);
        if (isFullScreen) {
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.CROSS, cross * 100);
            // size 只在渲染层缩放；同步用户尺寸会使旋转等操作重新生成更小的平铺单元。
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.SIZE, WATERMARK_TILE_BASE_SIZE);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.SPACE, space * 100);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.ROTATE, rotate);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.OFFSET_X, offsetX * 100);
            await this.setFullscreenOption(id, FULL_SCREEN_TYPE.OFFSET_Y, offsetY * 100);
        }
    }

    /** 初始化水印桥接实例 */
    private async initializedBridge(index?: number) {
        if (this.isInitialized && isUndefined(index)) return true;

        const frameId = index ?? this.framework.service.frame.getActiveFrameId();
        const mainLayer = this.framework.service.frame.getMainLayer(frameId);
        if (!mainLayer) return false;

        const { width, height } = mainLayer;
        const originCanvas = createCanvas(width, height);
        try {
            await waterMarkBridge.from(originCanvas).create(AR_RES_ID, fixZipResUrl(AR_RES_PATH));
            this.isInitialized = true;
        } catch (error) {
            console.error('水印工具初始化失败', error);
            this.isInitialized = false;
        }

        return this.isInitialized;
    }

    /** 创建水印信息 */
    private async createWatermarkInfo(id: string) {
        await waterMarkBridge.removeWaterMark(id);
        await waterMarkBridge.addWaterMark(id);
    }

    /** 设置图片水印信息 */
    private async setImgWatermarkInfo(layer: ILayerProps, url: string) {
        await waterMarkBridge.setCustomizePath(layer.id, url);
    }

    /** 设置文本水印信息 */
    private async setTextWatermarkInfo(layer: ILayerProps) {
        const id = layer.id;
        const { zipUrl } = layer.data as IWatermarkLayerProps;

        if (zipUrl) {
            const materialId = zipUrl.match(/zip\/(.*?).zip/)![1];
            await waterMarkBridge.setMaterialPath(layer.id, materialId, zipUrl, materialId);
        }

        let fontFamily = (layer.data as IWatermarkLayerProps).fontFamily;
        let texts: string[] = [];
        let rgba: number[] = [];
        if (fontFamily) {
            texts = (layer.data as IWatermarkLayerProps).texts;
            rgba = hexToRgbaArr((layer.data as IWatermarkLayerProps).textColor) as number[];
            rgba[3] = rgba[3] * 100;
        } else {
            const info = await waterMarkBridge.getTextsInfo(id);
            if (info == null || info.textLayers == undefined) return;
            // 默认文本长度
            const count = info.textLayers.length;
            if (count == 0) return;
            fontFamily = info.textLayers[0].info.mTextFont;
            texts = info.textLayers.map((v) => v.info.mTextString);
            rgba = [
                info.textLayers[0].info.mTextORgba.r * 255,
                info.textLayers[0].info.mTextORgba.g * 255,
                info.textLayers[0].info.mTextORgba.b * 255,
                info.textLayers[0].info.mTextORgba.a * 100,
            ];
            // Object.assign(layer.data, {
            //     texts,
            //     textColor: rgbaToHexa({
            //         r: info.textLayers[0].info.mTextORgba.r * 255,
            //         g: info.textLayers[0].info.mTextORgba.g * 255,
            //         b: info.textLayers[0].info.mTextORgba.b * 255,
            //         a: info.textLayers[0].info.mTextORgba.a,
            //     }),
            //     fontFamily,
            // });
        }
        await this.loadTextFamily(fontFamily);
        // await this.getWatermarkResult(id);
        await this.setTextWatermarkItems(id, texts, rgba, fontFamily);
        // await this.setTextWatermarkColor(
        //     id,
        //     {
        //         r: rgba[0],
        //         g: rgba[1],
        //         b: rgba[2],
        //         a: rgba[3] * 100,
        //     },
        //     texts.length,
        // );
        // await waterMarkBridge.updateResultBuffer(id);
        // return true;

        return {
            texts,
            rgba,
            fontFamily,
        };
    }

    /** 设置文本水印文案、颜色、字体 */
    private async setTextWatermarkItems(id: string, texts: any[], rgba: number[], fontFamily: string) {
        for (let i = WATERMARK_TEXTS_MAX - 1; i >= 0; i--) {
            await waterMarkBridge.removeText(id, i);
        }

        for (let i = 0; i < texts.length; i++) {
            const text = texts[i];
            await waterMarkBridge.addText(id, text);
            await waterMarkBridge.setTextColor(id, rgba[0], rgba[1], rgba[2], rgba[3], i);
            await waterMarkBridge.setTextFont(id, fontFamily, i);
        }
    }

    /** 获取水印结果 */
    private async getWatermarkResult(id: string) {
        return waterMarkBridge.getWaterMarkSplitBitmap(id);
    }

    /** 设置全屏选项 */
    private async setFullscreenOption(id: string, type: FULL_SCREEN_TYPE, value: number, layer?: ILayerProps) {
        if (type === FULL_SCREEN_TYPE.SIZE) {
            // if (layer) (layer.data as IWatermarkLayerProps).size = value / 100;
            await waterMarkBridge.setWidthRatio(id, value / 100, 0);
        } else if (type === FULL_SCREEN_TYPE.SPACE) {
            if (layer) (layer.data as IWatermarkLayerProps).space = value / 100;
            await waterMarkBridge.setSpacing(id, value / 100.0, 0);
        } else if (type === FULL_SCREEN_TYPE.ROTATE) {
            if (layer) layer.rotate = value;
            await waterMarkBridge.setRotate(id, value, 0);
        } else if (type === FULL_SCREEN_TYPE.CROSS) {
            if (layer) (layer.data as IWatermarkLayerProps).cross = value / 100;
            await waterMarkBridge.setStaggered(id, value / 100.0, 0);
        } else if (type === FULL_SCREEN_TYPE.OFFSET_X) {
            if (layer) (layer.data as IWatermarkLayerProps).offsetX = value / 100;
            await waterMarkBridge.setMoveOffsetX(id, value / 100.0);
        } else if (type === FULL_SCREEN_TYPE.OFFSET_Y) {
            if (layer) (layer.data as IWatermarkLayerProps).offsetY = value / 100;
            await waterMarkBridge.setMoveOffsetY(id, value / 100.0);
        }
    }
}

/**
 * 水印图层插件
 */
export const watermarkLayerPlugin: IExportPlugin = {
    sort: 0,
    type: PluginType.LAYER,
    name: LayerType.WATERMARK,
    createInstance({ identity }) {
        return [{ key: 'WatermarkLayer', value: new WatermarkLayer(identity) }];
    },
    // createState() {
    //     return [watermarkLayerState];
    // },
    createBehavior() {
        return watermarkLayerBehavior;
    },
    createHistory({ identity }) {
        return [new WatermarkLayerHistory(identity)];
    },
    // destroy({ getInstance }) {
    //     getInstance<WatermarkLayer>('WatermarkLayer').destroy();
    // },
};

/** WatermarkLayer 公共方法参数及 UI 限制使用的业务常量。 */
export { FULL_SCREEN_TYPE, WATERMARK_TEXTS_MAX, WATERMARK_TYPE } from './constants';
